import { DefaultMap, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import { logError } from "../debug-print";
import { GlideDateTime } from "../glide-date-time";
import { assert, compareStrings, defined, isArray, nullToUndefined, panic } from "../Support";
import type { MaybeForwardee } from "./component-helpers";
import {
    asArrayValue,
    asBasePrimitiveValue,
    asBoolean,
    asCallable,
    asNumber,
    asString,
    BaseForwardingValueProducer,
    ChainedEvalEnvironment,
    detectedRecursiveComputation,
    ForwardingValueProducer,
    getValueFromValueProducer,
    isLoadedDefined,
    isValueNotEmpty,
    loadedDefinedMap,
    loadedMap,
    RecomputingFromListValueProducer,
    RecomputingValueProducer,
    TwoPhaseRecomputingValueProducer,
} from "./component-helpers";
import type {
    ArgumentSpec,
    BasePrimitiveValue,
    Context,
    DeserializableClass,
    DeserializableKind,
    DeserializationSpec,
    EnvironmentBoundEvaluable,
    EvalEnvironment,
    Evaluable,
    ForceValueProducer,
    Loading,
    PotentialValue,
    RootEvalEnvironment,
    Value,
    ValueProducer,
} from "./computation-types";
import {
    BindEnvironmentEvaluable,
    Callable,
    CapturedEvalEnvironment,
    isPrimitiveValue,
    LOADING,
    makeContextBoundFunction,
} from "./computation-types";
import { Constant, Data } from "./data";

export class GetContextEvaluable<O, A> implements Evaluable<O, A> {
    public evalInEnvironment(_env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return context;
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["GetContext", "evaluable"];
    }
}

export class EnvironmentBoundValueProducer<O, A> implements EnvironmentBoundEvaluable<O, A> {
    constructor(private readonly _vp: ValueProducer<O, A>) {}

    public eval(_context: Context<O, A>): ValueProducer<O, A> {
        return this._vp;
    }

    public evalInEnvironment(_env: EvalEnvironment<O, A>, _context: Context<O, A>): ValueProducer<O, A> {
        return this._vp;
    }
}

class ContextBoundCallable<O, A> extends Callable<O, A> {
    constructor(private readonly _callable: Callable<O, A>, private readonly _context: Context<O, A>) {
        super();
    }

    public call(
        args: EnvironmentBoundEvaluable<O, A>[],
        _context: Context<O, A>,
        rootEnv: RootEvalEnvironment<O, A>
    ): ValueProducer<O, A> {
        return this._callable.call(args, this._context, rootEnv);
    }
}

class BindContextValueProducer<O, A> extends RecomputingValueProducer<O, A> {
    constructor(private readonly _value: ValueProducer<O, A>, private readonly _context: ValueProducer<O, A>) {
        super();
    }

    public recompute(force: ForceValueProducer<O, A>): PotentialValue<O, A> {
        const current = force(this._value);
        if (makeContextBoundFunction === undefined) {
            return panic("setIsOtherValue was never called");
        }
        if (!isLoadedDefined(current)) return current;
        const result = makeContextBoundFunction(current, this._context);
        if (result !== undefined) return result;
        if (current instanceof Callable) {
            return new ContextBoundCallable(current, this._context);
        }
        return current;
    }

    public set(_v: Value<O, A>): boolean {
        return panic("Cannot set a BindContext");
    }

    public addProperty(_name: string, _vp: ValueProducer<O, A>): void {
        return panic("Cannot add property to a BindContext");
    }

    public removeProperty(_name: string): void {
        return panic("Cannot remove property from a BindContext");
    }
}

class BindContextToValueEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _value: Evaluable<O, A>, private readonly _context: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const valueContext = this._context.evalInEnvironment(env, context);
        return new BindContextValueProducer(this._value.evalInEnvironment(env, valueContext), valueContext);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["BindContextToValue", "evaluable", ["value", "evaluable"], ["context", "evaluable"]];
    }
}

export interface BuiltinValueProducerClass<O, A> {
    new (appEnvironment: A, ...args: ValueProducer<O, A>[]): ValueProducer<O, A>;
}

export class BuiltinCallable<O, A> extends Callable<O, A> {
    constructor(private readonly _valueProducerClass: BuiltinValueProducerClass<O, A>) {
        super();
    }

    public call(
        args: EnvironmentBoundEvaluable<O, A>[],
        context: Context<O, A>,
        rootEnv: RootEvalEnvironment<O, A>
    ): ValueProducer<O, A> {
        return new this._valueProducerClass(rootEnv.appEnvironment, ...args.map(a => a.eval(context)));
    }
}

class CallValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    private _lastCallee: PotentialValue<O, A>;

    constructor(
        private readonly _callee: ValueProducer<O, A>,
        private readonly _args: EnvironmentBoundEvaluable<O, A>[],
        private readonly _context: ValueProducer<O, A>,
        private readonly _rootEnv: RootEvalEnvironment<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();

        this._callee.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        this._callee.unsubscribe(this);
        this._lastCallee = undefined;
    }

    protected recomputeForwardee(currentForwardee: MaybeForwardee<O, A>): MaybeForwardee<O, A> {
        const callee = this._callee.current;

        if (currentForwardee !== LOADING && currentForwardee !== undefined && callee === this._lastCallee) {
            return currentForwardee;
        }

        this._lastCallee = callee;

        if (callee === LOADING) return LOADING;
        return asCallable(callee).call(this._args, this._context, this._rootEnv);
    }
}

class CallOnceValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    private _result: MaybeForwardee<O, A> = LOADING;

    constructor(
        private readonly _callee: ValueProducer<O, A>,
        private readonly _args: EnvironmentBoundEvaluable<O, A>[],
        private readonly _context: ValueProducer<O, A>,
        private readonly _rootEnv: RootEvalEnvironment<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();

        this._callee.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        this._callee.unsubscribe(this);
    }

    protected recomputeForwardee(): MaybeForwardee<O, A> {
        if (isLoadedDefined(this._result)) {
            return this._result;
        }

        const callee = this._callee.current;
        if (!isLoadedDefined(callee)) {
            this._result = callee;
        } else {
            this._result = asCallable(callee).call(this._args, this._context, this._rootEnv);
        }

        return this._result;
    }
}

export class CallEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _callee: Evaluable<O, A>,
        private readonly _args: Evaluable<O, A>[],
        private readonly _once: Evaluable<O, A> | undefined
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const args = this._args.map(a => new BindEnvironmentEvaluable(a, env));
        if (this._callee instanceof BuiltinCallable) {
            return this._callee.call(args, context, env.root);
        }
        const once =
            loadedDefinedMap(
                definedMap(this._once, o => getValueFromValueProducer(o.evalInEnvironment(env, context))),
                asBoolean
            ) ?? false;
        if (once) {
            return new CallOnceValueProducer(this._callee.evalInEnvironment(env, context), args, context, env.root);
        } else {
            return new CallValueProducer(this._callee.evalInEnvironment(env, context), args, context, env.root);
        }
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return [
            "Call",
            "evaluable",
            ["callee", "evaluable"],
            ["args", "evaluable-array"],
            ["once", "evaluable", false],
        ];
    }
}

class LetEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _bindings: [string, Evaluable<O, A>][], private readonly _body: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const bindings = new Map<string, EnvironmentBoundEvaluable<O, A>>();

        // FIXME: We're depending here on `ChainedEvalEnvironment` not copying `bindings`.
        const newEnv = new ChainedEvalEnvironment(bindings, env);
        for (const [n, e] of this._bindings) {
            const vp = e.evalInEnvironment(newEnv, context);
            bindings.set(n, new EnvironmentBoundValueProducer(vp));
        }
        return this._body.evalInEnvironment(newEnv, context);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Let", "evaluable", ["bindings", "bindings"], ["body", "evaluable"]];
    }
}

class HoldEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _body: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, _context: Context<O, A>): ValueProducer<O, A> {
        return new Constant(new BindEnvironmentEvaluable(this._body, env));
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Hold", "evaluable", ["body", "evaluable"]];
    }
}

class GetForwardeeEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _value: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        let vp = this._value.evalInEnvironment(env, context);
        while (vp instanceof ForwardingValueProducer) {
            const forwardee = vp.getCurrentForwardee();
            if (!isLoadedDefined(forwardee)) break;
            vp = forwardee;
        }
        return vp;
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["GetForwardee", "evaluable", ["value", "evaluable"]];
    }
}

function asBindEnvironmentEvaluable<O, A>(
    vp: ValueProducer<O, A>,
    force: ForceValueProducer<O, A>
): BindEnvironmentEvaluable<O, A> | undefined {
    let current: PotentialValue<O, A>;
    if (vp instanceof Constant) {
        current = vp.current;
    } else if (vp instanceof Data) {
        current = force(vp);
    }
    if (current instanceof BindEnvironmentEvaluable) {
        return current;
    }
    return undefined;
}

class GetVariableEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _name: string) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, _context: Context<O, A>): ValueProducer<O, A> {
        const result = env.get(this._name);
        if (result === undefined) {
            return panic(`Variable not found in environment: ${this._name}`);
        }
        return result.eval(_context);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["GetVariable", "evaluable", ["name", "string"]];
    }
}

class GetPropertyValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    constructor(private readonly _obj: ValueProducer<O, A>, private readonly _property: string) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._obj.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._obj.unsubscribe(this);
    }

    protected recomputeForwardee(): ValueProducer<O, A> | Loading | undefined {
        const inspector = this._obj.getObjectInspector(undefined);
        if (!isLoadedDefined(inspector)) return inspector;
        // Missing properties are returned as undefined
        return inspector.getProperty(this._property);
    }

    public set(v: Value<O, A>): boolean {
        const forwardee = this.getCurrentForwardee();
        if (isLoadedDefined(forwardee)) {
            return forwardee.set(v);
        }

        this._obj.addProperty(this._property, new Data(v));
        return true;
    }
}

export function getPropertyValueProducer<O, A>(rowVP: ValueProducer<O, A>, propertyName: string): ValueProducer<O, A> {
    // Computed columns can be completely replaced, so we can't depend
    // on them continuing being part of the row object.  See comment in
    // `RowData.removePropertyWithoutUpdate`.
    //
    // Related: https://github.com/quicktype/glide/issues/7491

    // FIXME: Re-enable this once lazy computed column evaluation is tested:
    // const maybeVP = getPropertyValueProducerForRowData(rowVP, propertyName);
    // if (maybeVP instanceof Data) return maybeVP;

    return new GetPropertyValueProducer(rowVP, propertyName);
}

export class GetPropertyEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _obj: Evaluable<O, A>, private readonly _property: string) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const obj = this._obj.evalInEnvironment(env, context);
        return getPropertyValueProducer(obj, this._property);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["GetProperty", "evaluable", ["object", "evaluable"], ["property", "string"]];
    }
}

class GetDynamicPropertyValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    constructor(private readonly _obj: ValueProducer<O, A>, private readonly _property: ValueProducer<O, A>) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._obj.subscribe(this);
        this._property.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._obj.unsubscribe(this);
        this._property.unsubscribe(this);
    }

    protected recomputeForwardee(): ValueProducer<O, A> | Loading | undefined {
        const inspector = this._obj.getObjectInspector(undefined);
        if (!isLoadedDefined(inspector)) return inspector;
        const property = this._property.current;
        if (!isLoadedDefined(property)) return property;
        // Missing properties are returned as undefined
        return inspector.getProperty(asString(property));
    }

    public set(v: Value<O, A>): boolean {
        const forwardee = this.getCurrentForwardee();
        if (isLoadedDefined(forwardee)) {
            return forwardee.set(v);
        }

        const property = this._property.current;
        if (!isLoadedDefined(property)) {
            logError("Cannot set property because its name is not loaded or defined");
            return false;
        }

        this._obj.addProperty(asString(property), new Data(v));
        return true;
    }
}

class GetDynamicPropertyEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _obj: Evaluable<O, A>, private readonly _property: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const obj = this._obj.evalInEnvironment(env, context);
        const property = this._property.evalInEnvironment(env, context);
        return new GetDynamicPropertyValueProducer(obj, property);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["GetDynamicProperty", "evaluable", ["object", "evaluable"], ["property", "evaluable"]];
    }
}

class LookupFunctionEvaluable<O, A> implements Evaluable<O, A> {
    private _func: Evaluable<O, A> | undefined;

    constructor(private readonly _name: string) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        if (this._func === undefined) {
            this._func = defined(env.lookupUserDefinedComponent(this._name));
        }
        return this._func.evalInEnvironment(env.root, context);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["LookupFunction", "evaluable", ["name", "string"]];
    }
}

class CaptureEnvironmentEvaluable<O, A> implements Evaluable<O, A> {
    public evalInEnvironment(env: EvalEnvironment<O, A>, _context: Context<O, A>): ValueProducer<O, A> {
        return new Constant(new CapturedEvalEnvironment(env));
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["CaptureEnvironment", "evaluable"];
    }
}

export function asCapturedEvalEnvironment<O, A>(
    vp: ValueProducer<O, A>,
    force: ForceValueProducer<O, A>
): CapturedEvalEnvironment<O, A> | undefined {
    let current: PotentialValue<O, A>;
    if (vp instanceof Constant) {
        current = vp.current;
    } else if (vp instanceof Data) {
        current = force(vp);
    }
    if (current instanceof CapturedEvalEnvironment) {
        return current;
    }
    return undefined;
}

class IfValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    private _lastCondition: boolean | undefined;

    constructor(
        private readonly _condition: ValueProducer<O, A>,
        private readonly _then: EnvironmentBoundEvaluable<O, A>,
        private readonly _else: EnvironmentBoundEvaluable<O, A> | undefined,
        private readonly _context: ValueProducer<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._condition.subscribe(this);

        assert(this._lastCondition === undefined);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._condition.unsubscribe(this);

        this._lastCondition = undefined;
    }

    protected recomputeForwardee(currentForwardee: MaybeForwardee<O, A>): MaybeForwardee<O, A> {
        const { current } = this._condition;
        if (!isLoadedDefined(current)) {
            this._lastCondition = undefined;
            return current;
        }

        const condition = current !== false;
        if (this._lastCondition === condition) {
            if (currentForwardee !== LOADING) {
                return currentForwardee;
            } else {
                detectedRecursiveComputation();
            }
        }
        if (this.hasSubscribers) {
            this._lastCondition = condition;
        }

        if (condition) {
            return this._then.eval(this._context);
        } else {
            if (this._else === undefined) {
                return undefined;
            } else {
                return this._else.eval(this._context);
            }
        }
    }
}

class IfEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _condition: Evaluable<O, A>,
        private readonly _then: Evaluable<O, A>,
        private readonly _else: Evaluable<O, A> | undefined
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new IfValueProducer(
            this._condition.evalInEnvironment(env, context),
            new BindEnvironmentEvaluable(this._then, env),
            definedMap(this._else, e => new BindEnvironmentEvaluable(e, env)),
            context
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["If", "evaluable", ["condition", "evaluable"], ["then", "evaluable"], ["else", "evaluable", false]];
    }
}

class SwitchValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    constructor(
        private readonly _value: ValueProducer<O, A>,
        private readonly _cases: [EnvironmentBoundEvaluable<O, A>, EnvironmentBoundEvaluable<O, A>][],
        private readonly _fallback: EnvironmentBoundEvaluable<O, A>,
        private readonly _context: ValueProducer<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();
        this._value.subscribe(this);
        this._context.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();
        this._value.unsubscribe(this);
        this._context.unsubscribe(this);
    }

    protected recomputeForwardee(): ValueProducer<O, A> | Loading {
        const value = this._value.current;
        if (value === LOADING) return LOADING;
        for (const [comparand, result] of this._cases) {
            const c = comparand.eval(this._context).current;
            if (value === c) {
                return result.eval(this._context);
            }
        }
        return this._fallback.eval(this._context);
    }
}

class SwitchEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _value: Evaluable<O, A>,
        private readonly _cases: [Evaluable<O, A>, Evaluable<O, A>][],
        private readonly _fallback: Evaluable<O, A>
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const cases = this._cases.map(([c, r]): [EnvironmentBoundEvaluable<O, A>, EnvironmentBoundEvaluable<O, A>] => [
            new BindEnvironmentEvaluable(c, env),
            new BindEnvironmentEvaluable(r, env),
        ]);
        return new SwitchValueProducer(
            this._value.evalInEnvironment(env, context),
            cases,
            new BindEnvironmentEvaluable(this._fallback, env),
            context
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Switch", "evaluable", ["value", "evaluable"], ["cases", "kvp-array"], ["fallback", "evaluable"]];
    }
}

class PinValueProducer<O, A> extends BaseForwardingValueProducer<O, A> {
    private _didSubscribe: boolean = false;

    constructor(private readonly _value: ValueProducer<O, A>) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();

        if (!this._didSubscribe) {
            this._value.subscribe(this);
            this._didSubscribe = true;
        }
    }

    protected getCurrentForwardee(): MaybeForwardee<O, A> {
        return this._value;
    }

    public setDirty(): void {
        this.setListenersDirty();
    }
}

class PinEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _value: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new PinValueProducer(this._value.evalInEnvironment(env, context));
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Pin", "evaluable", ["value", "evaluable"]];
    }
}

class MakeListEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _items: Evaluable<O, A>[]) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new Data(this._items.map(e => e.evalInEnvironment(env, context)));
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["MakeList", "evaluable", ["items", "evaluable-array"]];
    }
}

class MakeObjectEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _properties: [Evaluable<O, A>, Evaluable<O, A>][]) {}

    // FIXME: Make this a proper value producer.  We must subscribe to the
    // keys.
    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const object: { [key: string]: ValueProducer<O, A> } = {};
        for (const [k, v] of this._properties) {
            const maybeKey = getValueFromValueProducer(k.evalInEnvironment(env, context));
            if (maybeKey === LOADING) continue;
            object[asString(maybeKey)] = v.evalInEnvironment(env, context);
        }
        return new Data(object);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["MakeObject", "evaluable", ["properties", "kvp-array"]];
    }
}

class MakeObjectFromPairsEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _pairs: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const object: { [key: string]: ValueProducer<O, A> } = {};

        const pairs = getValueFromValueProducer(this._pairs.evalInEnvironment(env, context));
        // FIXME: We should handle the `LOADING` case, but we'd have to return a
        // VP that's LOADING.
        if (pairs !== LOADING) {
            for (const pairVP of asArrayValue(pairs)) {
                const pair = loadedDefinedMap(pairVP.current, asArrayValue);
                if (pair?.length !== 2) continue;

                const [keyVP, valueVP] = pair;
                const key = loadedDefinedMap(keyVP.current, asString);
                if (key === undefined) continue;

                object[key] = valueVP;
            }
        }

        return new Data(object);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["MakeObjectFromPairs", "evaluable", ["pairs", "evaluable"]];
    }
}

class ClonePrimitiveEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _source: Evaluable<O, A>,
        private readonly _defaultValue: Evaluable<O, A> | undefined
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const source = getValueFromValueProducer(this._source.evalInEnvironment(env, context));
        if (!isLoadedDefined(source)) {
            const defaultValue = definedMap(this._defaultValue, e =>
                getValueFromValueProducer(e.evalInEnvironment(env, context))
            );
            if (!isLoadedDefined(defaultValue)) {
                return new Data<O, A>(undefined);
            }
            return new Data(defaultValue);
        }
        return new Data(source);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["ClonePrimitive", "evaluable", ["source", "evaluable"], ["defaultValue", "evaluable", false]];
    }
}

class ClonePrimitivesEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _source: Evaluable<O, A>,
        private readonly _properties: ReadonlyArray<[string, any]>
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const sourceObject = this._source
            .evalInEnvironment(env, context)
            .getObjectInspector(undefined)
            ?.getObjectValue();
        const object: { [key: string]: ValueProducer<O, A> } = {};
        for (const [name, defaultValue] of this._properties) {
            let v = loadedMap(sourceObject, s => definedMap(s?.[name], getValueFromValueProducer));
            if (v === LOADING || v === undefined) {
                v = defaultValue;
            } else {
                if (typeof defaultValue === "string") {
                    v = (v as any).toString();
                }
            }
            object[name] = new Data(nullToUndefined(v));
        }
        return new Data(object);
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["ClonePrimitives", "evaluable", ["source", "evaluable"], ["properties", "string-json-array"]];
    }
}

class MergeObjectsEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _first: Evaluable<O, A>, private readonly _second: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        const first = this._first.evalInEnvironment(env, context).getObjectInspector(undefined)?.getObjectValue();
        const second = this._second.evalInEnvironment(env, context).getObjectInspector(undefined)?.getObjectValue();
        return new Data<O, A>({ ...first, ...second });
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["MergeObjects", "evaluable", ["first", "evaluable"], ["second", "evaluable"]];
    }
}

// mapper -> item -> outcome
type MapperCache<O, A> = Map<EnvironmentBoundEvaluable<O, A>, Map<ValueProducer<O, A>, ValueProducer<O, A>>>;

class MappedValueProducerCache<O, A> {
    private _map: MapperCache<O, A> = new Map();

    private evalMapper(
        newMap: MapperCache<O, A>,
        mapper: EnvironmentBoundEvaluable<O, A>,
        item: ValueProducer<O, A>,
        force: ForceValueProducer<O, A>
    ): ValueProducer<O, A> {
        const maybeRecur = (result: ValueProducer<O, A>) => {
            const ebe = asBindEnvironmentEvaluable(result, force);
            if (ebe === undefined) return result;
            return this.evalMapper(newMap, ebe, item, force);
        };

        function set(result: ValueProducer<O, A>) {
            let m = newMap.get(mapper);
            if (m === undefined) {
                m = new Map();
                newMap.set(mapper, m);
            }
            m.set(item, result);
        }

        const newOutcomeMap = newMap.get(mapper);
        if (newOutcomeMap !== undefined) {
            const newOutcome = newOutcomeMap.get(item);
            if (newOutcome !== undefined) {
                return maybeRecur(newOutcome);
            }
        }

        const oldOutcomeMap = this._map.get(mapper);
        if (oldOutcomeMap !== undefined) {
            const oldOutcome = oldOutcomeMap.get(item);
            if (oldOutcome !== undefined) {
                set(oldOutcome);
                return maybeRecur(oldOutcome);
            }
        }

        const outcome = mapper.eval(item);
        set(outcome);
        return maybeRecur(outcome);
    }

    public forEachItemInList<T>(
        list: readonly ValueProducer<O, A>[],
        mapper: EnvironmentBoundEvaluable<O, A>,
        force: ForceValueProducer<O, A>,
        f: (item: ValueProducer<O, A>, mapped: ValueProducer<O, A>) => T | undefined
    ): T | undefined {
        const newMap: MapperCache<O, A> = new Map();
        let result: T | undefined;

        for (const item of list) {
            const outcome = this.evalMapper(newMap, mapper, item, force);
            result = f(item, outcome);
            if (result !== undefined) break;
        }

        this._map = newMap;

        return result;
    }

    public mapList(
        list: readonly ValueProducer<O, A>[],
        mapper: EnvironmentBoundEvaluable<O, A>,
        force: ForceValueProducer<O, A>
    ): readonly ValueProducer<O, A>[] {
        const outcomes: ValueProducer<O, A>[] = [];
        this.forEachItemInList(list, mapper, force, (_item, outcome) => {
            outcomes.push(outcome);
            return undefined;
        });
        return outcomes;
    }
}

export class FilterValueProducer<O, A> extends TwoPhaseRecomputingValueProducer<O, A> {
    private _cache = new MappedValueProducerCache<O, A>();

    constructor(
        private readonly _predicate: EnvironmentBoundEvaluable<O, A>,
        private readonly _list: ValueProducer<O, A>
    ) {
        super();
    }

    protected recomputeValueProducers(
        force: ForceValueProducer<O, A>
    ): readonly ValueProducer<O, A>[] | Loading | undefined {
        const list = force(this._list);
        if (!isLoadedDefined(list)) return list;

        return this._cache.mapList(asArrayValue(list), this._predicate, force);
    }

    protected recomputeFromValueProducers(vps: readonly ValueProducer<O, A>[]): PotentialValue<O, A> {
        const list = this._list.current;
        if (!isLoadedDefined(list)) return list;

        const arr = asArrayValue(list);
        const len = vps.length;
        assert(arr.length === len);

        const filtered: ValueProducer<O, A>[] = [];
        for (let i = 0; i < len; i++) {
            const outcome = vps[i].current;
            if (outcome !== LOADING && asBoolean(outcome)) {
                filtered.push(arr[i]);
            }
        }
        return filtered;
    }

    public appendItem(vp: ValueProducer<O, A>): void {
        this._list.appendItem(vp);
    }

    public removeLastItem(): void {
        this._list.removeLastItem();
    }

    public setDirty(sender: ValueProducer<O, A>): void {
        if (sender === this._list) {
            this.setDependenciesDirty();
        } else {
            this.setValuesDirty();
        }
    }
}

export class FilterEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _predicate: Evaluable<O, A>, private readonly _list: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new FilterValueProducer(
            new BindEnvironmentEvaluable(this._predicate, env),
            this._list.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Filter", "evaluable", ["predicate", "evaluable"], ["list", "evaluable"]];
    }
}

// FIXME: Rewrite using a subscription manager and `MappedValueProducerCache`.
// We can't directly use `RecomputingFromListValueProducer`, because this needs
// to forward a VP, not just produce a value.
export class FindValueProducer<O, A> extends ForwardingValueProducer<O, A> {
    private _outcomes: Map<ValueProducer<O, A>, ValueProducer<O, A>> = new Map();

    constructor(
        private readonly _predicate: EnvironmentBoundEvaluable<O, A>,
        private readonly _list: ValueProducer<O, A>
    ) {
        super();
    }

    protected firstSubscribed(): void {
        super.firstSubscribed();

        this._list.subscribe(this);
    }

    protected lastUnsubscribed(): void {
        super.lastUnsubscribed();

        for (const vp of this._outcomes.values()) {
            vp.unsubscribe(this);
        }
        this._outcomes.clear();

        this._list.unsubscribe(this);
    }

    protected recomputeForwardee(): MaybeForwardee<O, A> {
        const list = this._list.current;
        if (!isLoadedDefined(list)) return list;

        const { hasSubscribers } = this;

        const newOutcomes = new Map<ValueProducer<O, A>, ValueProducer<O, A>>();
        let result: ValueProducer<O, A> | undefined;

        for (const item of asArrayValue(list)) {
            let outcome = newOutcomes.get(item);
            if (outcome === undefined) {
                outcome = this._outcomes.get(item);
                if (outcome === undefined) {
                    outcome = this._predicate.eval(item);
                    if (hasSubscribers) {
                        outcome.subscribe(this);
                    }
                }
                newOutcomes.set(item, outcome);
            }

            const outcomeValue = outcome.current;
            if (outcomeValue !== LOADING && asBoolean(outcomeValue)) {
                result = item;
                break;
            }
        }

        if (hasSubscribers) {
            const newOutcomesSet = new Set<ValueProducer<O, A>>(newOutcomes.values());
            for (const vp of this._outcomes.values()) {
                if (newOutcomesSet.has(vp)) continue;
                vp.unsubscribe(this);
            }
            this._outcomes = newOutcomes;
        }

        return result;
    }
}

class FindEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _predicate: Evaluable<O, A>, private readonly _list: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new FindValueProducer(
            new BindEnvironmentEvaluable(this._predicate, env),
            this._list.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Find", "evaluable", ["predicate", "evaluable"], ["list", "evaluable"]];
    }
}

class SomeValueProducer<O, A> extends RecomputingFromListValueProducer<O, A> {
    private _cache = new MappedValueProducerCache<O, A>();

    constructor(private readonly _predicate: EnvironmentBoundEvaluable<O, A>, list: ValueProducer<O, A>) {
        super(undefined, list);
    }

    public recomputeFromList(
        list: readonly ValueProducer<O, A>[],
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        const result = this._cache.forEachItemInList(list, this._predicate, force, (_item, outcome) => {
            const outcomeValue = force(outcome);
            if (outcomeValue !== LOADING && asBoolean(outcomeValue)) return true;
            return undefined;
        });
        return result ?? false;
    }
}

class SomeEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _predicate: Evaluable<O, A>, private readonly _list: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new SomeValueProducer(
            new BindEnvironmentEvaluable(this._predicate, env),
            this._list.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Some", "evaluable", ["predicate", "evaluable"], ["list", "evaluable"]];
    }
}

function compareValuesPrimitive(one: any, two: any): number {
    if (one === two) return 0;
    if (one < two) return -1;
    return 1;
}

export function arePrimitivesEqual<O, A>(one: Value<O, A>, two: Value<O, A>): boolean {
    if (one === two) return true;
    if (one instanceof GlideDateTime) {
        return one.compareTo(two) === 0;
    }
    if (two instanceof GlideDateTime) {
        return two.compareTo(one) === 0;
    }
    if (one instanceof Date && two instanceof Date) {
        return one.getTime() === two.getTime();
    }
    return false;
}

export function compareValues<O, A>(one: Value<O, A>, two: Value<O, A>): number | undefined {
    if (one === two) return 0;

    // `undefined` is only equal to `undefined` and the empty string
    if (one === undefined) {
        if (two === "") return 0;
        return undefined;
    }
    if (two === undefined) {
        if (one === "") return 0;
        return undefined;
    }

    // GlideDateTime internally implements its own compareTo.
    // Unfortunately nothing else interacts properly with GlideDateTime.
    //
    // If the right hand side is a GlideDateTime then we preserve expected
    // behavior by simply negating the resulting comparison.
    if (one instanceof GlideDateTime) {
        return one.compareTo(two);
    } else if (two instanceof GlideDateTime) {
        const reversed = two.compareTo(one);
        return reversed === 0 ? 0 : -reversed;
    }

    if (!isPrimitiveValue(one) || !isPrimitiveValue(two)) return undefined;

    const oneType = typeof one;
    const twoType = typeof two;

    // If the values are of the same type, just compare them directly below.
    // If they're not, this gets us to a common type.
    if (oneType !== twoType) {
        const newOne = asString(one);
        const newTwo = asString(two);
        return compareStrings(newOne, newTwo);
    } else if (oneType === "string") {
        return compareStrings(one as string, two as string);
    } else if (one instanceof Date && two instanceof Date) {
        one = one.getTime();
        two = two.getTime();
    }

    return compareValuesPrimitive(one, two);
}

export function areValuesEqual<O, A>(one: Value<O, A>, two: Value<O, A>): boolean | undefined {
    if (one === two) return true;

    // `undefined` is only equal to `undefined` and the empty string, but we will
    // treat the result of testing for equality as always defined in this case.
    if (one === undefined) {
        return two === "";
    }
    if (two === undefined) {
        return one === "";
    }

    const result = compareValues(one, two);
    if (result === undefined) return undefined;
    return result === 0;
}

export class SortValueProducer<O, A> extends RecomputingFromListValueProducer<O, A> {
    private _cache = new MappedValueProducerCache<O, A>();

    constructor(
        private readonly _key: EnvironmentBoundEvaluable<O, A>,
        private readonly _order: ValueProducer<O, A>,
        list: ValueProducer<O, A>
    ) {
        super(undefined, list);
    }

    public recomputeFromList(
        list: readonly ValueProducer<O, A>[],
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        let itemsAndKeys: [ValueProducer<O, A>, Value<O, A>][] = [];

        this._cache.forEachItemInList(list, this._key, force, (item, key) => {
            const keyValue = force(key);
            itemsAndKeys.push([item, keyValue === LOADING ? undefined : keyValue]);
            return undefined;
        });

        // This is the old computation model copy of ##sorting.
        itemsAndKeys = itemsAndKeys.sort(([, one], [, two]) => {
            if (typeof one === "string") {
                one = one.trim().toLowerCase();
            }
            if (typeof two === "string") {
                two = two.trim().toLowerCase();
            }
            const result = compareValues(one, two);
            if (result !== undefined) {
                return result;
            }
            if (one === undefined) {
                return -1;
            } else {
                return 1;
            }
        });

        if (force(this._order) === "desc") {
            itemsAndKeys = itemsAndKeys.reverse();
        }

        return itemsAndKeys.map(([i]) => i);
    }

    public appendItem(vp: ValueProducer<O, A>): void {
        this._container.appendItem(vp);
    }

    public removeLastItem(): void {
        this._container.removeLastItem();
    }
}

class SortEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _key: Evaluable<O, A>,
        private readonly _order: Evaluable<O, A>,
        private readonly _list: Evaluable<O, A>
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new SortValueProducer(
            new BindEnvironmentEvaluable(this._key, env),
            this._order.evalInEnvironment(env, context),
            this._list.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Sort", "evaluable", ["key", "evaluable"], ["order", "evaluable"], ["list", "evaluable"]];
    }
}

export abstract class MappedListValueProducer<O, A> extends RecomputingFromListValueProducer<O, A> {
    private _cache = new MappedValueProducerCache<O, A>();

    constructor(private readonly _mapper: EnvironmentBoundEvaluable<O, A>, list: ValueProducer<O, A>) {
        super(undefined, list);
    }

    protected abstract recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A>;

    protected recomputeFromList(
        list: readonly ValueProducer<O, A>[],
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        return this.recomputeWithForEach(f => this._cache.forEachItemInList(list, this._mapper, force, f), force);
    }
}

interface MappedListValueProducerClass<O, A> {
    new (_mapper: EnvironmentBoundEvaluable<O, A>, list: ValueProducer<O, A>): ValueProducer<O, A>;
}

abstract class MappedListEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _mapper: Evaluable<O, A>, private readonly _list: Evaluable<O, A>) {}

    protected abstract get valueProducerClass(): MappedListValueProducerClass<O, A>;

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new this.valueProducerClass(
            new BindEnvironmentEvaluable(this._mapper, env),
            this._list.evalInEnvironment(env, context)
        );
    }
}

function makeDeserializationSpecForMappedListEvaluable(
    name: string,
    mapperName: string = "mapper",
    listName: string = "list",
    ...moreArgs: readonly ArgumentSpec[]
): DeserializationSpec<DeserializableKind> {
    return [name, "evaluable", [mapperName, "evaluable"], [listName, "evaluable"], ...moreArgs];
}

class MapListValueProducer<O, A> extends MappedListValueProducer<O, A> {
    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined
    ): PotentialValue<O, A> {
        const outcomes: ValueProducer<O, A>[] = [];
        forEach((_item, outcome) => {
            outcomes.push(outcome);
            return undefined;
        });
        return outcomes;
    }
}

class MapListEvaluable<O, A> extends MappedListEvaluable<O, A> {
    protected get valueProducerClass() {
        return MapListValueProducer;
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("MapList");
    }
}

class GroupListValueProducer<O, A> extends RecomputingFromListValueProducer<O, A> {
    private _cache = new MappedValueProducerCache<O, A>();

    constructor(private readonly _key: EnvironmentBoundEvaluable<O, A>, list: ValueProducer<O, A>) {
        super(undefined, list);
    }

    public recomputeFromList(
        list: readonly ValueProducer<O, A>[],
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        const glideDateTimeInterning = new Map<number, GlideDateTime>();
        const groups = new DefaultMap<BasePrimitiveValue | Date | GlideDateTime, ValueProducer<O, A>[]>(() => []);
        this._cache.forEachItemInList(list, this._key, force, (item, key) => {
            const keyValue = force(key);
            if (keyValue instanceof Date || keyValue instanceof GlideDateTime) {
                const asGlideDateTime = keyValue instanceof Date ? GlideDateTime.fromDate(keyValue) : keyValue;
                const epochTime = asGlideDateTime.asDate().getTime();
                let interned = glideDateTimeInterning.get(epochTime);
                if (interned === undefined) {
                    interned = asGlideDateTime;
                    glideDateTimeInterning.set(epochTime, interned);
                }
                groups.get(interned).push(item);
            } else if (isLoadedDefined(keyValue)) {
                groups.get(asBasePrimitiveValue(keyValue)).push(item);
            }
            return undefined;
        });

        const outcomes: ValueProducer<O, A>[] = [];
        for (const [key, items] of groups) {
            outcomes.push(new Data({ key: new Data<O, A>(key), items: new Data<O, A>(items) }));
        }
        return outcomes;
    }
}

class GroupListEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _mapper: Evaluable<O, A>, private readonly _list: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new GroupListValueProducer(
            new BindEnvironmentEvaluable(this._mapper, env),
            this._list.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["GroupList", "evaluable", ["key", "evaluable"], ["list", "evaluable"]];
    }
}

class SumValueProducer<O, A> extends MappedListValueProducer<O, A> {
    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        let sum = 0;
        forEach((_item, number) => {
            const numberValue = force(number);
            if (!isLoadedDefined(numberValue)) return undefined;
            sum += asNumber(numberValue);
            return undefined;
        });
        return sum;
    }
}

class SumEvaluable<O, A> implements Evaluable<O, A> {
    constructor(private readonly _number: Evaluable<O, A>, private readonly _list: Evaluable<O, A>) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new SumValueProducer(
            new BindEnvironmentEvaluable(this._number, env),
            this._list.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["Sum", "evaluable", ["number", "evaluable"], ["list", "evaluable"]];
    }
}

class UniqueValuesValueProducer<O, A> extends MappedListValueProducer<O, A> {
    constructor(
        mapper: EnvironmentBoundEvaluable<O, A>,
        list: ValueProducer<O, A>,
        private readonly _wholeResult: ValueProducer<O, A> | undefined
    ) {
        super(mapper, list);
    }

    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        const glideDateTimeInterning = new Map<number, GlideDateTime>();
        const values = new Set<BasePrimitiveValue | Date | GlideDateTime>();
        const items: ValueProducer<O, A>[] = [];

        function addItem(v: Value<O, A>, i: ValueProducer<O, A>) {
            if (isValueNotEmpty(v) !== true) return;

            if (v instanceof Date || v instanceof GlideDateTime) {
                const asGlideDateTime = v instanceof Date ? GlideDateTime.fromDate(v) : v;
                const epochTime = asGlideDateTime.asDate().getTime();
                let interned = glideDateTimeInterning.get(epochTime);
                if (interned === undefined) {
                    interned = asGlideDateTime;
                    glideDateTimeInterning.set(epochTime, interned);
                }
                if (values.has(interned)) return;
                values.add(interned);
                items.push(i);
            } else {
                const asPrimitive = asBasePrimitiveValue(v);
                if (values.has(asPrimitive)) return;
                values.add(asPrimitive);
                items.push(i);
            }
        }

        let haveLoading = false;
        forEach((item, outcome) => {
            const s = force(outcome);
            if (s === LOADING) {
                haveLoading = true;
                return;
            }
            if (isArray(s)) {
                for (const svp of s) {
                    const t = force(svp);
                    if (t === LOADING) {
                        haveLoading = true;
                        continue;
                    }
                    if (t === undefined) continue;
                    addItem(t, item);
                }
            } else if (s !== undefined) {
                addItem(s, item);
            }
        });
        if (values.size === 0 && haveLoading) return LOADING;

        const wholeResult = definedMap(this._wholeResult, force);
        if (isLoadedDefined(wholeResult) && asBoolean(wholeResult)) {
            return items;
        }

        return Array.from(values).map(v => new Constant<O, A>(v));
    }
}

class UniqueValuesEvaluable<O, A> {
    constructor(
        private readonly _mapper: Evaluable<O, A>,
        private readonly _list: Evaluable<O, A>,
        private readonly _wholeItem: Evaluable<O, A> | undefined
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new UniqueValuesValueProducer(
            new BindEnvironmentEvaluable(this._mapper, env),
            this._list.evalInEnvironment(env, context),
            definedMap(this._wholeItem, e => e.evalInEnvironment(env, context))
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("UniqueValues", undefined, undefined, [
            "wholeItem",
            "evaluable",
            false,
        ]);
    }
}

class AllEqualValueProducer<O, A> extends MappedListValueProducer<O, A> {
    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        let first: Value<O, A>;
        let haveFirst = false;

        const maybeResult = forEach((_item, outcome) => {
            const v = force(outcome);
            if (v === LOADING) return LOADING;

            if (!haveFirst) {
                first = v;
                haveFirst = true;
                return undefined;
            } else if (areValuesEqual(first, v) !== true) {
                return false;
            } else {
                return undefined;
            }
        });

        if (maybeResult !== undefined) {
            return maybeResult;
        } else {
            return true;
        }
    }
}

class AllEqualEvaluable<O, A> extends MappedListEvaluable<O, A> {
    protected get valueProducerClass() {
        return AllEqualValueProducer;
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("AllEqual");
    }
}

class AllTrueValueProducer<O, A> extends MappedListValueProducer<O, A> {
    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        let haveLoading = false;
        const result = forEach((_item, outcome) => {
            const b = force(outcome);
            if (b === LOADING) {
                haveLoading = true;
            } else if (!asBoolean(b)) {
                return false;
            }
            return undefined;
        });

        if (result === false) return false;
        if (haveLoading) return LOADING;
        return true;
    }
}

class AllTrueEvaluable<O, A> extends MappedListEvaluable<O, A> {
    protected get valueProducerClass() {
        return AllTrueValueProducer;
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("AllTrue");
    }
}

class CountValueProducer<O, A> extends MappedListValueProducer<O, A> {
    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        let count = 0;
        const result = forEach((_item, outcome) => {
            const b = force(outcome);
            if (b === LOADING) {
                return LOADING;
            } else if (asBoolean(b)) {
                count += 1;
            }
            return undefined;
        });
        if (result === LOADING) return LOADING;
        return count;
    }
}

class CountEvaluable<O, A> extends MappedListEvaluable<O, A> {
    protected get valueProducerClass() {
        return CountValueProducer;
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("Count");
    }
}

class SomeTrueValueProducer<O, A> extends MappedListValueProducer<O, A> {
    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        let haveLoading = false;
        const result = forEach((_item, outcome) => {
            const b = force(outcome);
            if (b === LOADING) {
                haveLoading = true;
            } else if (asBoolean(b)) {
                return true;
            }
            return undefined;
        });

        if (result === true) return true;
        if (haveLoading) return LOADING;
        return false;
    }
}

class SomeTrueEvaluable<O, A> extends MappedListEvaluable<O, A> {
    protected get valueProducerClass() {
        return SomeTrueValueProducer;
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("SomeTrue");
    }
}

// FIXME: Technically this is a forwarding VP.  It would be nice if we could
// use the mapping abstraction without using `MappedListValueProducer`.
class MinMaxValueProducer<O, A> extends MappedListValueProducer<O, A> {
    constructor(
        mapper: EnvironmentBoundEvaluable<O, A>,
        list: ValueProducer<O, A>,
        private readonly _result: EnvironmentBoundEvaluable<O, A> | undefined,
        private readonly _switchSign: number
    ) {
        super(mapper, list);
    }

    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        let minimumValue: Value<O, A> = undefined;
        let minimumItem: ValueProducer<O, A> | undefined = undefined;
        const result = forEach((item, outcome) => {
            const b = force(outcome);
            if (b === LOADING) {
                return LOADING;
            } else if (minimumValue === undefined) {
                minimumValue = b;
                minimumItem = item;
            } else {
                const compare = compareValues(b, minimumValue);
                if (compare === undefined) return undefined;
                if (compare * this._switchSign < 0) {
                    minimumValue = b;
                    minimumItem = item;
                }
            }
            return undefined;
        });
        if (result === LOADING) return LOADING;
        if (minimumItem === undefined) return undefined;
        if (this._result === undefined) {
            return minimumItem;
        } else {
            return force(this._result.eval(minimumItem));
        }
    }
}

class MinimumEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _mapper: Evaluable<O, A>,
        private readonly _list: Evaluable<O, A>,
        private readonly _result: Evaluable<O, A> | undefined
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new MinMaxValueProducer(
            new BindEnvironmentEvaluable(this._mapper, env),
            this._list.evalInEnvironment(env, context),
            definedMap(this._result, r => new BindEnvironmentEvaluable(r, env)),
            1
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("Minimum", "mapper", "list", [
            "result",
            "evaluable",
            false,
        ]);
    }
}

class MaximumEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _mapper: Evaluable<O, A>,
        private readonly _list: Evaluable<O, A>,
        private readonly _result: Evaluable<O, A> | undefined
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new MinMaxValueProducer(
            new BindEnvironmentEvaluable(this._mapper, env),
            this._list.evalInEnvironment(env, context),
            definedMap(this._result, r => new BindEnvironmentEvaluable(r, env)),
            -1
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("Maximum", "mapper", "list", [
            "result",
            "evaluable",
            false,
        ]);
    }
}

class JoinStringsValueProducer<O, A> extends MappedListValueProducer<O, A> {
    constructor(
        mapper: EnvironmentBoundEvaluable<O, A>,
        list: ValueProducer<O, A>,
        private readonly _separator: ValueProducer<O, A>
    ) {
        super(mapper, list);
    }

    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        const separator = force(this._separator);
        if (separator === LOADING) return LOADING;

        const strings: string[] = [];
        forEach((_item, vp) => {
            const stringValue = force(vp);
            if (!isLoadedDefined(stringValue)) return undefined;
            strings.push(asString(stringValue));
            return undefined;
        });
        return strings.join(asString(separator));
    }
}

class JoinStringsEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _mapper: Evaluable<O, A>,
        private readonly _list: Evaluable<O, A>,
        private readonly _separator: Evaluable<O, A>
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new JoinStringsValueProducer(
            new BindEnvironmentEvaluable(this._mapper, env),
            this._list.evalInEnvironment(env, context),
            this._separator.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable("JoinStrings", undefined, undefined, [
            "separator",
            "evaluable",
        ]);
    }
}

class BuildChoicesValueProducer<O, A> extends MappedListValueProducer<O, A> {
    constructor(
        mapper: EnvironmentBoundEvaluable<O, A>,
        list: ValueProducer<O, A>,
        private readonly _existingChoices: ValueProducer<O, A>,
        private readonly _addChoices: ValueProducer<O, A> | undefined,
        private readonly _removeChoices: ValueProducer<O, A> | undefined
    ) {
        super(mapper, list);
    }

    protected recomputeWithForEach(
        forEach: <T>(f: (item: ValueProducer<O, A>, outcome: ValueProducer<O, A>) => T | undefined) => T | undefined,
        force: ForceValueProducer<O, A>
    ): PotentialValue<O, A> {
        function fromArrayVP(arrayVP: ValueProducer<O, A> | undefined): readonly Value<O, A>[] {
            if (arrayVP === undefined) return [];
            const array = force(arrayVP);
            if (!isArray(array)) return [];
            return mapFilterUndefined(array, vp => {
                const v = force(vp);
                if (!isPrimitiveValue(v)) return undefined;
                return v;
            });
        }

        const includeChoicesSet = new Set<string>();
        for (const v of fromArrayVP(this._existingChoices)) {
            includeChoicesSet.add(asString(v));
        }
        for (const v of fromArrayVP(this._addChoices)) {
            includeChoicesSet.add(asString(v));
        }
        for (const v of fromArrayVP(this._removeChoices)) {
            includeChoicesSet.delete(asString(v));
        }
        const includeChoices = Array.from(includeChoicesSet);

        const results: ValueProducer<O, A>[] = [];
        forEach((item, vp) => {
            const v = force(vp);
            if (isLoadedDefined(v) && includeChoices.some(c => areValuesEqual(c, v))) {
                results.push(item);
            }
        });
        return results;
    }
}

class BuildChoicesEvaluable<O, A> implements Evaluable<O, A> {
    constructor(
        private readonly _mapper: Evaluable<O, A>,
        private readonly _list: Evaluable<O, A>,
        private readonly _existingChoices: Evaluable<O, A>,
        private readonly _addChoices: Evaluable<O, A>,
        private readonly _removeChoices: Evaluable<O, A>
    ) {}

    public evalInEnvironment(env: EvalEnvironment<O, A>, context: Context<O, A>): ValueProducer<O, A> {
        return new BuildChoicesValueProducer(
            new BindEnvironmentEvaluable(this._mapper, env),
            this._list.evalInEnvironment(env, context),
            this._existingChoices.evalInEnvironment(env, context),
            this._addChoices.evalInEnvironment(env, context),
            this._removeChoices.evalInEnvironment(env, context)
        );
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return makeDeserializationSpecForMappedListEvaluable(
            "BuildChoices",
            undefined,
            undefined,
            ["existing", "evaluable"],
            ["add", "evaluable", false],
            ["remove", "evaluable", false]
        );
    }
}

class ActionSourceEvaluable<O, A> implements Evaluable<O, A> {
    public evalInEnvironment(env: EvalEnvironment<O, A>): ValueProducer<O, A> {
        return new Data<O, A>(env.actionSource ?? "unknown");
    }

    static get spec(): DeserializationSpec<DeserializableKind> {
        return ["ActionSource", "evaluable"];
    }
}

export function registerPrimitives<O, A>(registerDeserializable: (c: DeserializableClass<O, A>) => void): void {
    registerDeserializable(Data);
    registerDeserializable(Constant);
    registerDeserializable(GetContextEvaluable);
    registerDeserializable(BindContextToValueEvaluable);
    registerDeserializable(LetEvaluable);
    registerDeserializable(HoldEvaluable);
    registerDeserializable(GetForwardeeEvaluable);
    registerDeserializable(CallEvaluable);
    registerDeserializable(GetVariableEvaluable);
    registerDeserializable(GetPropertyEvaluable);
    registerDeserializable(GetDynamicPropertyEvaluable);
    registerDeserializable(LookupFunctionEvaluable);
    registerDeserializable(CaptureEnvironmentEvaluable);
    registerDeserializable(IfEvaluable);
    registerDeserializable(SwitchEvaluable);
    registerDeserializable(PinEvaluable);
    registerDeserializable(MakeListEvaluable);
    registerDeserializable(MakeObjectEvaluable);
    registerDeserializable(MakeObjectFromPairsEvaluable);
    registerDeserializable(ClonePrimitiveEvaluable);
    registerDeserializable(ClonePrimitivesEvaluable);
    registerDeserializable(MergeObjectsEvaluable);
    registerDeserializable(FilterEvaluable);
    registerDeserializable(FindEvaluable);
    registerDeserializable(SomeEvaluable);
    registerDeserializable(SortEvaluable);
    registerDeserializable(MapListEvaluable);
    registerDeserializable(GroupListEvaluable);
    registerDeserializable(SumEvaluable);
    registerDeserializable(AllTrueEvaluable);
    registerDeserializable(SomeTrueEvaluable);
    registerDeserializable(UniqueValuesEvaluable);
    registerDeserializable(AllEqualEvaluable);
    registerDeserializable(ActionSourceEvaluable);
    registerDeserializable(CountEvaluable);
    registerDeserializable(MinimumEvaluable);
    registerDeserializable(MaximumEvaluable);
    registerDeserializable(JoinStringsEvaluable);
    registerDeserializable(BuildChoicesEvaluable);
}
